import type { KeyboardEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";

import {
  ArrowLeftIcon,
  CircleAlertIcon,
  CircleQuestionMarkIcon,
  KeyIcon,
  LockIcon,
  MailIcon,
} from "lucide-react";

import { type LoginMfaChallenge, isLoginMfaRequired } from "simplydo/core/mfa";
import SearchParams from "simplydo/core/searchParams";
import { OpenAPI } from "simplydo/interfaces";
import { DotPulse } from "simplydo/react/components/DotPulse";
import { styled } from "simplydo/react/styled";
import {
  AppleIcon,
  GoogleIcon,
  LinkedInIcon,
  MicrosoftIcon,
} from "simplydo/react/ui/brand-icon";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { H2, H3, H4, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Input } from "simplydo/react/ui/input";
import { Loader } from "simplydo/react/ui/loader";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import constants from "utils/constants";
import util from "utils/utils";

import { MfaLoginFlow } from "components/lib/Mfa/MfaLoginFlow";

import OrganisationChooser from "./OrganisationChooser";
import {
  type UrlOrganisation,
  isMagicLoginEmailResponse,
  organisationLogoUrl,
} from "./types";
const SSOButtons = styled.div`
  ${Button} {
    color: #fff !important;
  }
`;

type AccountResponse = OpenAPI.GET<"/accounts">["response"];

type LoginProps = { embedded?: boolean };

export function BlockedOrganisationLanding({
  organisation,
}: {
  organisation: UrlOrganisation;
}) {
  const { t } = useTranslation();
  const orgName = organisation.appName || organisation.name;
  const blockedMessage =
    organisation.blockedMessage?.trim() ||
    t("entry.blocked.default", { orgName: organisation.name });

  const dispatch = useAppDispatch();
  const onSelectOrganisation = useCallback(
    (org: UrlOrganisation | null) =>
      dispatch(actions.organisations.receiveUrlOrganisation(org)),
    [dispatch],
  );

  return (
    <div>
      <Image
        size="small"
        src={organisationLogoUrl(organisation)}
        alt=""
        style={{ margin: "0 auto" }}
      />
      <H2>
        Notice for {orgName || util.appName()}
        <Subheader style={{ whiteSpace: "pre-wrap" }}>
          {blockedMessage}
        </Subheader>
      </H2>
      <Divider />
      <ButtonLink to="/" onClick={() => onSelectOrganisation(null)}>
        <Icon icon={ArrowLeftIcon} />
        {t("entry.blocked.selectDifferentOrg")}
      </ButtonLink>
    </div>
  );
}

function Login({ embedded }: LoginProps) {
  const [mfaChallenge, setMfaChallenge] = useState<LoginMfaChallenge | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [securityPhrase, setSecurityPhrase] = useState("");
  const [loginExchangeToken, setLoginExchangeToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSSO, setLoadingSSO] = useState("");

  const [accountDetails, setAccountDetails] = useState<AccountResponse | null>(
    null,
  );

  const passwordRef = useRef(null);

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const paramMessage = params.get("message");
  const ssoProtectionToken = params.get("ssoProtectionToken") || "";

  const dispatch = useAppDispatch();
  const onSelectOrganisation = useCallback(
    (org: UrlOrganisation | null) =>
      dispatch(actions.organisations.receiveUrlOrganisation(org)),
    [dispatch],
  );
  const onUpdateEmail = useCallback(
    (value: string) => dispatch(actions.entry.updateEmail(value)),
    [dispatch],
  );
  const onUpdateChallengeResponse = useCallback(
    (value: string) => dispatch(actions.entry.updateChallengeResponse(value)),
    [dispatch],
  );

  const authErrorMessage = useAppSelector((state) => state.auth.errorMessage);
  const utmLink = useAppSelector((state) => state.auth.utmLink);
  const utmChallenge = useAppSelector((state) => state.auth.utmChallenge);
  const email = useAppSelector((state) => state.entry.email);
  const challengeResponse = useAppSelector(
    (state) => state.entry.challengeResponse,
  );

  const organisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const shouldAutoLogin =
    !organisation?.isBlocked &&
    organisation?.code &&
    organisation?.auth?.types?.length === 1 &&
    organisation?.auth?.types[0] !== "standard";

  useEffect(() => {
    if (shouldAutoLogin && organisation?.auth?.types?.length) {
      const authType = organisation.auth.types[0];
      if (!authType || !organisation.code) {
        return;
      }
      const redirectAuthTypes = ["microsoft", "linkedin", "apple", "google"];
      if (redirectAuthTypes.includes(authType)) {
        setLoadingSSO(authType);
        util.loginWithOAuth(authType, organisation.code);
      } else if (authType === "saml") {
        setLoadingSSO(authType);
        util.loginWithSaml(organisation.code);
      }
    }
  }, [shouldAutoLogin, organisation?.code, organisation?.auth?.types]);

  useEffect(() => {
    const exchangeTokenChecker = setInterval(() => {
      if (loginExchangeToken && document.visibilityState === "visible") {
        api.auth.loginWithToken(
          loginExchangeToken,
          () => {
            clearInterval(exchangeTokenChecker);
          },
          () => {},
        );
      }
    }, 2000);
    return function cleanup() {
      clearInterval(exchangeTokenChecker);
    };
  }, [loginExchangeToken]);

  const authIsEnabled = (authMethod: string) => {
    // Special case for ssoProtection, which allows standard login when redirected from SSO protection check
    if (paramMessage === "ssoProtectionLoginRequired") {
      return authMethod === "standard";
    }
    return (organisation?.auth?.types as string[] | undefined)?.includes(
      authMethod,
    );
  };

  const ssoIsEnabled = () => {
    // Disable all sso login methods when redirected from SSO protection check
    if (paramMessage === "ssoProtectionLoginRequired") {
      return false;
    }
    const authMethods = organisation?.auth?.types || [];
    return (
      authMethods.length > 1 ||
      (authMethods.length === 1 && authMethods[0] !== "standard")
    );
  };

  const checkAccount = useCallback(
    (withEmail?: string) => {
      const emailToCheck = withEmail || email;
      setErrorMessage("");

      if (!emailToCheck || !emailToCheck.match(constants.emailRegex)) return;
      if (!organisation) {
        return;
      }
      setLoading(true);
      api.auth.verifyAccount(
        organisation._id,
        emailToCheck,
        challengeResponse ?? "",
        (data) => {
          setLoading(false);
          setAccountDetails(data);
        },
        (err) => {
          setErrorMessage(err.message);
          setLoading(false);
        },
      );
    },
    [challengeResponse, email, organisation],
  );

  useEffect(() => {
    const emailParam = params.get("email");
    if (emailParam && organisation) {
      params.delete("email");
      setParams(params);
      onUpdateEmail(emailParam);
    }
  }, [onUpdateEmail, setParams, params, organisation]);

  const finishLogin = useCallback(() => {
    setMfaChallenge(null);
    setLoading(false);
    const then = params.get("then");
    if (then) {
      navigate(then);
    }
  }, [navigate, params]);

  const login = () => {
    setLoading(true);
    setErrorMessage("");
    api.auth.login(
      {
        email,
        password,
        challengeResponse: challengeResponse ?? undefined,
        org: organisation?._id,
        utmLink,
        ssoProtectionToken,
      },
      (data) => {
        if (isLoginMfaRequired(data)) {
          setLoading(false);
          setMfaChallenge(data);
          return;
        }
        setLoading(false);
        const then = params.get("then");
        if (then) {
          navigate(then);
        }
      },
      (err) => {
        setLoading(false);
        setErrorMessage(err.message);
      },
    );
  };

  const loginMagicLink = useCallback(() => {
    setLoading(true);
    setErrorMessage("");
    api.auth.requestLoginEmail(
      email,
      (data) => {
        if (!isMagicLoginEmailResponse(data)) {
          setLoading(false);
          setErrorMessage("Unable to request magic login email.");
          return;
        }
        setLoading(false);
        setSecurityPhrase(data.securityPhrase);
        setLoginExchangeToken(data.exchangeToken);
      },
      (err) => {
        setLoading(false);
        setErrorMessage(err.message);
      },
    );
  }, [email]);

  if (!organisation) {
    return <OrganisationChooser />;
  }

  if (organisation.isBlocked) {
    return <BlockedOrganisationLanding organisation={organisation} />;
  }

  if (securityPhrase) {
    return (
      <div style={{ textAlign: "center" }}>
        <DotPulse size={12} duration={2} />
        <H3>
          Waiting for email authorisation...
          <Subheader>
            <p>
              We&apos;ve sent you an email with a link to authorise this login.
              If you haven&apos;t received it, please check your spam folder.
            </p>
            <p>
              <strong>
                Please do not close this window or refresh the page.
              </strong>
            </p>
          </Subheader>
        </H3>

        <Divider />
        <H4>The email should contain this security phrase:</H4>
        <div
          style={{
            marginTop: 5,
            padding: 20,
            background: "rgb(230,230,230)",
            borderRadius: 10,
            textAlign: "center",
          }}
        >
          <strong>{securityPhrase}</strong>
        </div>
      </div>
    );
  }

  if (shouldAutoLogin) {
    return (
      <>
        <H2>Redirecting to login...</H2>
        <Divider />
        <Loader />
        <div style={{ height: 5 }} />
      </>
    );
  }

  if (
    authIsEnabled("standard") &&
    !organisation.loginLinkEnabled &&
    accountDetails &&
    !accountDetails.exists &&
    accountDetails.challengePassed
  ) {
    const urlParams = new SearchParams();
    if (params.get("utm-link") || utmLink) {
      urlParams.set("utm-link", params.get("utm-link") || utmLink || "");
    }
    if (params.get("utm-challenge") || utmChallenge) {
      urlParams.set(
        "utm-challenge",
        params.get("utm-challenge") || utmChallenge || "",
      );
    }

    const thenParam = params.get("then");
    if (thenParam && thenParam !== "/") {
      urlParams.set("then", encodeURIComponent(thenParam));
    } else {
      const path = location.pathname + location?.search;
      if (path && path !== "/" && path !== "/login") {
        urlParams.set("then", encodeURIComponent(path));
      }
    }

    return <Navigate to={`/register${urlParams.toSafeString()}`} />;
  }

  const WelcomeHeading = embedded ? H4 : H2;

  return (
    <div>
      <Image
        size="small"
        src={organisationLogoUrl(organisation)}
        alt=""
        style={{ margin: "0 auto" }}
      />
      <WelcomeHeading style={{ marginBottom: 12 }}>
        {organisation?.appName || organisation?.name
          ? t("entry.login.welcome", {
              orgName: organisation?.appName || organisation?.name,
            })
          : `Welcome to ${util.appName()}`}
        {organisation?.welcomeText ? (
          <Subheader style={{ whiteSpace: "pre-wrap" }}>
            {organisation.welcomeText}
          </Subheader>
        ) : null}
      </WelcomeHeading>
      {paramMessage && paramMessage === "ssoProtectionLoginRequired" ? (
        <Message color="blue">
          We detected that you are trying to access {util.appName()} using an
          email you had already used before. Please log in with your existing
          account details.
        </Message>
      ) : null}
      {authErrorMessage ? (
        <Message color="blue">
          We need to verify your details to confirm it&apos;s you. Please log in
          again.
        </Message>
      ) : null}
      {!util.localStorageIsSupported() ? (
        <Message color="orange">
          Your device does not support saving data locally. Your login
          information will not be saved when revisiting {util.appName()}.
        </Message>
      ) : null}

      <SSOButtons style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {authIsEnabled("saml") && (
          <Button
            size="small"
            fluid
            loading={loadingSSO === "saml"}
            onClick={() => {
              setLoadingSSO("saml");
              util.loginWithSaml(organisation.code);
            }}
            color="primary"
          >
            <Icon icon={KeyIcon} />
            {organisation.auth?.saml?.buttonText ?? t("entry.login.sso")}
          </Button>
        )}
        {authIsEnabled("linkedin") && (
          <Button
            size="small"
            fluid
            color="linkedin"
            loading={loadingSSO === "linkedin"}
            onClick={() => {
              setLoadingSSO("linkedin");
              util.loginWithOAuth("linkedin", organisation.code);
            }}
          >
            <Icon icon={LinkedInIcon} />
            Sign in with LinkedIn
          </Button>
        )}
        {authIsEnabled("google") && (
          <Button
            size="small"
            fluid
            color="google plus"
            loading={loadingSSO === "google"}
            onClick={() => {
              setLoadingSSO("google");
              util.loginWithOAuth("google", organisation.code);
            }}
          >
            <Icon icon={GoogleIcon} />
            Sign in with Google
          </Button>
        )}
        {authIsEnabled("apple") && (
          <Button
            size="small"
            fluid
            color="black"
            loading={loadingSSO === "apple"}
            onClick={() => {
              setLoadingSSO("apple");
              util.loginWithOAuth("apple", organisation.code);
            }}
          >
            <Icon icon={AppleIcon} />
            Sign in with Apple
          </Button>
        )}
        {authIsEnabled("microsoft") && (
          <Button
            size="small"
            fluid
            // @ts-ignore - msteams allowed via AppStyling override
            color="msteams"
            loading={loadingSSO === "microsoft"}
            onClick={() => {
              setLoadingSSO("microsoft");
              util.loginWithOAuth("microsoft", organisation.code);
            }}
          >
            <Icon icon={MicrosoftIcon} />
            {organisation.auth?.microsoft?.buttonText ??
              "Sign in with Microsoft"}
          </Button>
        )}
      </SSOButtons>

      {authIsEnabled("standard") && (
        <>
          {ssoIsEnabled() && <Divider horizontal>OR</Divider>}
          <H5>Enter your email address to continue</H5>
          {errorMessage && !loading && (
            <Message size="small" color="orange">
              <Icon icon={CircleAlertIcon} />
              <MessageHeader>{errorMessage}</MessageHeader>
            </Message>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <Input
              fluid
              tabIndex={0}
              value={email}
              icon={MailIcon}
              onChange={(e) => {
                onUpdateEmail(e.target.value);
                setPassword("");
                onUpdateChallengeResponse("");
                setAccountDetails(null);
                setErrorMessage("");
              }}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.keyCode === 13) {
                  e.preventDefault();
                  e.stopPropagation();
                  if (
                    accountDetails?.exists &&
                    accountDetails.challengePassed
                  ) {
                    login();
                  } else {
                    checkAccount();
                  }
                }
              }}
              name="email"
              type="email"
              autoComplete="email"
              placeholder={t("placeholder.email")}
            />

            {accountDetails ? (
              <>
                {organisation.loginLinkEnabled ? (
                  accountDetails.existsInOrganisation ? (
                    <Button
                      tabIndex={0}
                      onClick={loginMagicLink}
                      fluid
                      loading={loading}
                      color="primary"
                    >
                      Send magic login link
                    </Button>
                  ) : (
                    <Message color="orange" style={{ marginTop: 0 }}>
                      <MessageHeader>Unable to find your account</MessageHeader>
                      <MessageContent>
                        This email address is not registered with{" "}
                        {organisation.name}. Please check that it is correct or
                        retry with another email.
                      </MessageContent>
                    </Message>
                  )
                ) : null}

                {!organisation.loginLinkEnabled ? (
                  <>
                    {accountDetails.customChallengeRequired ? (
                      <Input
                        autoFocus
                        fluid
                        icon={CircleQuestionMarkIcon}
                        label={organisation?.registrationChallenge?.question}
                        defaultValue={challengeResponse ?? ""}
                        onChange={(e) =>
                          onUpdateChallengeResponse(e.target.value)
                        }
                      />
                    ) : null}

                    {!accountDetails.hasStandardAuth &&
                    !accountDetails.customChallengeRequired ? (
                      <Message color="blue" style={{ marginTop: 0 }}>
                        <MessageHeader>Password setup required</MessageHeader>
                        <MessageContent>
                          To continue signing into {organisation.name} please
                          set up a password first. You will receive an email
                          with instructions to complete this step.
                          <Divider />
                          <Button
                            loading={loading}
                            onClick={() => {
                              setLoading(true);
                              api.auth.sendPasswordSetupEmail(
                                email,
                                organisation?._id,
                                () => {
                                  setLoading(false);
                                  toast.success(t("entry.setupPassword.sent"));
                                  setAccountDetails((prev) => ({
                                    ...prev,
                                    hasStandardAuth: true,
                                  }));
                                  setErrorMessage(
                                    "Please check your email for password setup instructions before proceeding.",
                                  );
                                },
                                (err) => {
                                  setLoading(false);
                                  toast.error(err.message);
                                },
                              );
                            }}
                            color="primary"
                          >
                            Request password setup email
                          </Button>
                        </MessageContent>
                      </Message>
                    ) : null}

                    {accountDetails.hasStandardAuth ? (
                      <>
                        <div
                          style={{
                            visibility:
                              accountDetails.exists &&
                              accountDetails.challengePassed
                                ? "visible"
                                : "hidden",
                            maxHeight:
                              accountDetails.exists &&
                              accountDetails.challengePassed
                                ? "unset"
                                : "0px",
                          }}
                        >
                          <Input
                            tabIndex={0}
                            defaultValue={password}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => setPassword(e.target.value)}
                            fluid
                            icon={LockIcon}
                            input={{ ref: passwordRef }}
                            name="password"
                            type="password"
                            placeholder={t("entry.login.yourPass")}
                            onKeyDown={(e: KeyboardEvent) => {
                              if (e.keyCode === 13) {
                                e.preventDefault();
                                e.stopPropagation();
                                login();
                              }
                            }}
                          />
                        </div>
                        {accountDetails.exists &&
                        accountDetails.challengePassed ? (
                          <Button
                            tabIndex={0}
                            fluid
                            loading={loading}
                            onClick={login}
                            name="login-button"
                            disabled={password.length === 0}
                            color="primary"
                          >
                            {t("entry.login.action")}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}

            {!accountDetails || accountDetails.customChallengeRequired ? (
              <Button
                tabIndex={0}
                fluid
                loading={loading}
                onClick={() => checkAccount()}
                disabled={!email || !email.match(constants.emailRegex)}
                name="continue-button"
                color="primary"
              >
                {t("generic.continue")}
              </Button>
            ) : null}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Link
                style={{ cursor: "pointer" }}
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  onSelectOrganisation(null);
                  setAccountDetails(null);
                }}
              >
                Not your organisation?
              </Link>
              {!organisation.loginLinkEnabled &&
              accountDetails &&
              accountDetails.hasStandardAuth ? (
                <Link tabIndex={0} to="/password/forgotten">
                  {t("entry.forgottenPassword.title")}
                </Link>
              ) : null}
            </div>
          </div>
        </>
      )}

      {mfaChallenge ? (
        <MfaLoginFlow
          key={mfaChallenge.exchangeToken}
          email={email}
          password={password}
          organisationId={organisation?._id}
          challenge={mfaChallenge}
          loading={loading}
          setLoading={setLoading}
          onSuccess={finishLogin}
          onCancel={() => {
            setMfaChallenge(null);
            setErrorMessage("");
            setLoading(false);
          }}
        />
      ) : null}
    </div>
  );
}

export default Login;
